import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import css from './AutocompletePopover.module.scss';
import type { CaretPoint } from '../types/basilTypes';
import type { CompletionItem } from '../../../../dorian-shared/types/episodeScript';

/** The gap the list keeps from the caret and from the edge of the viewport. */
const MARGIN = 8;

/** Where the list was put, and whether it had to go above the caret to fit. */
export type PopoverPlacement = { left: number; top: number; flipped: boolean };

/**
 * The placement, as arithmetic rather than as a side effect, so the flip decision is testable
 * without a browser (editor.js:686-706). Below the caret by default; above it when it would fall off
 * the bottom; clamped horizontally either way.
 */
export function placePopover(
  point: Pick<CaretPoint, 'left' | 'top' | 'bottom'>,
  size: { width: number; height: number },
  viewport: { width: number; height: number },
): PopoverPlacement {
  const left = Math.max(MARGIN, Math.min(point.left, viewport.width - size.width - MARGIN));
  let top = point.bottom + 2;
  let flipped = false;
  if (top + size.height > viewport.height - MARGIN) {
    const above = point.top - size.height - 2;
    flipped = above >= MARGIN;
    top = flipped ? above : Math.max(MARGIN, viewport.height - size.height - MARGIN);
  }

  return { left: Math.round(left), top: Math.round(top), flipped };
}

/** What the popover needs. It renders nothing at all when there is nothing to offer. */
export type AutocompletePopoverProps = {
  /** The offered items. An empty list never renders. */
  items: CompletionItem[];
  /** The item the arrow keys are on. */
  active: number;
  /** The caret's pixel box, measured from the mirror. */
  point: CaretPoint | null;
  /** Accept an item. A click accepts; the caret never leaves the script. */
  onAccept: (index: number) => void;
  /** The element the list is portalled into: the modal dialog, or the body if it cannot be found. */
  container: Element | null;
  /** The id the textarea's `aria-controls` points at. */
  listboxId: string;
};

/**
 * The list is portalled out of the editor frame because the frame and the text stack are both
 * `overflow: hidden` and a list drawn inside them is clipped (editor.css:39, :50). Inside a Modal
 * the parent is the dialog element rather than the body, so the list sits inside the Modal's own
 * stacking context and its focus trap.
 */
export function AutocompletePopover(props: AutocompletePopoverProps) {
  const {
    items,
    active,
    point,
    onAccept,
    container,
    listboxId,
  } = props;
  const popoverRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    const popover = popoverRef.current;
    if (!popover || !point) {
      return;
    }
    const viewport = {
      width: typeof window === 'undefined' ? 1024 : window.innerWidth,
      height: typeof window === 'undefined' ? 768 : window.innerHeight,
    };
    const placement = placePopover(
      point,
      { width: popover.offsetWidth, height: popover.offsetHeight },
      viewport,
    );
    popover.style.left = `${placement.left}px`;
    popover.style.top = `${placement.top}px`;
    popover.dataset.flipped = placement.flipped ? 'true' : 'false';
    popover.classList.toggle(css.flipped, placement.flipped);
  }, [point, items, active]);

  useEffect(() => {
    const list = listRef.current;
    const option = list ? list.children[active] : null;
    if (!list || !(option instanceof HTMLElement)) {
      return;
    }
    // The list scrolls inside itself when it has to. The surface never does.
    const bottom = option.offsetTop + option.offsetHeight;
    if (option.offsetTop < list.scrollTop) {
      list.scrollTop = option.offsetTop;
    } else if (bottom > list.scrollTop + list.clientHeight) {
      list.scrollTop = bottom - list.clientHeight;
    }
  }, [active, items]);

  if (items.length === 0 || !point || !container) {
    return null;
  }

  return ReactDOM.createPortal(
    <div className={css.popover} ref={popoverRef} data-testid="basil-complete">
      <ul className={css.list} id={listboxId} role="listbox" aria-label="Suggestions" ref={listRef}>
        {items.map((item, index) => (
          <li
            className={[css.option, index === active ? css.active : ''].filter(Boolean).join(' ')}
            role="option"
            id={`${listboxId}-item-${index}`}
            key={`${item.kind}-${item.label}`}
            aria-selected={index === active}
            title={item.detail ?? ''}
            // The caret must never leave the script, so the press is swallowed and the click accepts.
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => onAccept(index)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onAccept(index);
              }
            }}
          >
            <span className={css.label}>{item.label}</span>
            <span className={css.detail}>{item.kind}</span>
          </li>
        ))}
      </ul>
      <p className={css.hint}>↑↓ to move · Enter or Tab to pick · Esc to close</p>
    </div>,
    container,
  );
}
